<template>
  <div class="page-heading">
    <h2>App Usage Report</h2>
    <div class="action-bar">
      <select
        class="form-select"
        v-model="formValue.service_type"
        style="width: 120px"
      >
        <option value="B2C">B2C</option>
        <option value="B2B">B2B</option>
      </select>
      <Datepicker v-model="date" :enableTimePicker="false" :clearable="false" />
      <select
        class="form-select"
        v-model="formValue.shift"
        style="width: 180px"
      >
        <option value="morning">Morning Trips</option>
        <option value="evening">Evening Trips</option>
      </select>
      <button
        v-if="syncLoading"
        class="btn btn-primary"
        style="white-space: nowrap"
        type="button"
        disabled
      >
        <span
          class="spinner-border spinner-border-sm"
          role="status"
          aria-hidden="true"
        ></span>
        Syncing...
      </button>
      <button
        v-else
        class="btn btn-primary"
        style="white-space: nowrap"
        @click="manualSyncAttendanceData()"
      >
        <i class="bi bi-arrow-repeat"></i> Sync
      </button>
    </div>
  </div>
  <div class="page-content">
    <div class="usage-count">
      <div class="count">
        <div
          class="card text-white bg-primary"
          @click="
            selectedDriverCat = 'all';
            selectedAttendance = attendance.total;
          "
        >
          <div class="card-body">
            <p class="card-title">Total Driver</p>
            <h5 class="card-text">
              {{ attendance.total?.length }}
            </h5>
          </div>
        </div>
      </div>
      <div class="count">
        <div
          class="card text-white bg-info"
          @click="
            selectedDriverCat = 'app_used';
            selectedAttendance = attendance.appUsed;
          "
        >
          <div class="card-body">
            <p class="card-title">App Used</p>
            <h5 class="card-text">
              {{ attendance.appUsed?.length }} ({{ appUsagePercentage }}%)
            </h5>
          </div>
        </div>
      </div>
      <div class="count">
        <div
          class="card text-white bg-success"
          @click="
            selectedDriverCat = 'present';
            selectedAttendance = attendance.present;
          "
        >
          <div class="card-body">
            <p class="card-title">Present Count</p>
            <h5 class="card-text">{{ attendance.present.length }}</h5>
          </div>
        </div>
      </div>
      <div class="count">
        <div
          class="card text-white bg-danger"
          @click="
            selectedDriverCat = 'absent';
            selectedAttendance = attendance.absent;
          "
        >
          <div class="card-body">
            <p class="card-title">Absent Count</p>
            <h5 class="card-text">{{ attendance.absent.length }}</h5>
          </div>
        </div>
      </div>
      <div class="count">
        <div
          class="card text-white bg-warning"
          @click="
            selectedDriverCat = 'pending';
            selectedAttendance = attendance.pending;
          "
        >
          <div class="card-body">
            <p class="card-title">Pending (Partial)</p>
            <h5 class="card-text">{{ attendance.pending.length }}</h5>
          </div>
        </div>
      </div>
    </div>
    <div class="usage-details">
      <div class="card partner-card">
        <div class="card-header">
          <span v-if="selectedDriverCat === 'all'">All Drivers</span>
          <span v-else-if="selectedDriverCat === 'app_used'"
            >Drivers who used the App</span
          >
          <span v-else-if="selectedDriverCat === 'present'"
            >Present Drivers</span
          >
          <span v-else-if="selectedDriverCat === 'absent'">Absent Drivers</span>
          <span v-else-if="selectedDriverCat === 'pending'"
            >Pending Drivers</span
          >
        </div>
        <DefaultLoader v-if="isLoading"></DefaultLoader>
        <div class="card-body" v-else>
          <CustomDataTable
            v-if="selectedAttendance?.length > 0"
            :searchParams="[
              'name',
              'mobile',
              'trip_start_time',
              'trip_end_time'
            ]"
            :filteredData="selectedAttendance"
            @fetchPaginatedData="fetchPaginatedData"
          >
            <table class="table">
              <thead>
                <tr>
                  <th>SL</th>
                  <th>Name</th>
                  <th>Phone No</th>
                  <th>Trip Start Time</th>
                  <th>Trip End Time</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(attendance, index) in paginatedAttendanceList"
                  :key="index"
                >
                  <td>{{ index + 1 }}</td>
                  <td>
                    {{ attendance.name }}
                  </td>
                  <td>
                    {{ attendance.mobile }}
                  </td>
                  <td>
                    {{ attendance.trip_start_time }}
                  </td>
                  <td>
                    {{ attendance.trip_end_time }}
                  </td>
                </tr>
              </tbody>
            </table>
          </CustomDataTable>
          <div class="alert alert-danger text-center" v-else>No one!</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker';
import http from '../config/axios';
import format from '../store/format';
import userServiceAPI from '../store/userServiceAPI';
import CustomDataTable from '@/components/customDataTable/CustomDataTable.vue';
import helpers from '../store/helpers';

export default {
  components: {
    Datepicker,
    CustomDataTable
  },
  data() {
    return {
      isLoading: false,
      syncLoading: false,
      formValue: {
        service_type: localStorage.getItem('serviceType'),
        date: null,
        shift: 'morning'
      },
      date: new Date(),
      appUsageReport: [],
      attendance: {
        total: [],
        appUsed: [],
        present: [],
        pending: [],
        absent: []
      },
      selectedDriverCat: 'all',
      selectedAttendance: null,
      allUserCodes: [],
      userInfoObj: {},
      paginatedAttendanceList: []
    };
  },
  mounted() {
    this.formatDate();
  },
  watch: {
    date() {
      this.formatDate();
    },
    formValue: {
      handler: function () {
        this.getAppUsageReport();
      },
      deep: true
    }
  },
  computed: {
    appUsagePercentage() {
      let total = this.attendance.total?.length;
      let appUsed = this.attendance.appUsed?.length;
      let appUsagePercentage = (100 / total) * appUsed;

      return helpers.isNumeric(appUsagePercentage)
        ? Math.round(appUsagePercentage)
        : 0;
    }
  },
  methods: {
    formatDate() {
      this.formValue.date = format.date(this.date, 'YYYY-MM-DD', null);
    },
    emptyAttendanceData() {
      this.attendance = {
        total: [],
        appUsed: [],
        present: [],
        pending: [],
        absent: []
      };
    },
    getAppUsageReport() {
      this.isLoading = true;
      http
        .get(
          `v2/partners/attendance?daily_app_usage_report=1&app_usage_date=${this.formValue.date}&app_usage_shift=${this.formValue.shift}&app_usage_service_type=${this.formValue.service_type}`
        )
        .then((response) => {
          this.appUsageReport = response.data.data
            ? response.data.data.daily_app_usage_report
            : [];
          this.countAttendanceData();
          this.selectedDriverCat = 'all';
          this.selectedAttendance = this.attendance.total;

          userServiceAPI
            .getUserDetails(this.allUserCodes)
            .then((result) => {
              this.isLoading = false;
              this.userInfoObj = {};
              this.userInfoObj = userServiceAPI.mapUsersIntoObject(
                result.data?.data
              );
              this.refactorAttendanceData();
            })
            .catch(() => {
              this.isLoading = false;
            });
        })
        .catch((error) => {
          this.isLoading = false;
          this.$partnerSwal.errorToast.fire({
            title: 'Error!',
            text: error.response?.data?.message
          });
        });
    },
    countAttendanceData() {
      this.allUserCodes = [];
      this.emptyAttendanceData();

      this.appUsageReport.forEach((element) => {
        if (element.driverCode) {
          this.allUserCodes.push(element.driverCode);
        }
        if (element.totalTrip) {
          this.attendance.total.push(element);
        }
        if (element.app_usage) {
          this.attendance.appUsed.push(element);
        }
        if (element.Present) {
          this.attendance.present.push(element);
        } else if (element.Pending) {
          this.attendance.pending.push(element);
        } else if (element.Absent) {
          this.attendance.absent.push(element);
        }
      });
    },
    refactorAttendanceData() {
      this.selectedAttendance.forEach((attendance) => {
        if (this.userInfoObj[attendance.driverCode]) {
          attendance.name = this.userInfoObj[attendance.driverCode].name;
          attendance.mobile = this.userInfoObj[attendance.driverCode].mobile;
        } else {
          attendance.name = 'N/A';
          attendance.mobile = 'N/A';
        }

        attendance.trip_start_time = '';
        attendance.trip_end_time = '';
        if (attendance.usersDailyTripTimes) {
          attendance.usersDailyTripTimes.forEach((time, index) => {
            attendance.trip_start_time += index ? ', ' : '';
            attendance.trip_start_time += format.time(time.trip_start_time);
            attendance.trip_end_time += index ? ', ' : '';
            attendance.trip_end_time += format.time(time.trip_end_time);
          });
        }
      });
    },
    fetchPaginatedData(paginatedData) {
      this.paginatedAttendanceList = paginatedData;
    },
    manualSyncAttendanceData() {
      this.syncLoading = true;
      let startDate = this.formValue.date;
      let endDate = this.formValue.date;

      http
        .post(
          `/v2/partners/attendance/manual-sync?start_date=${startDate}
        &end_date=${endDate}&serviceType=${this.formValue.service_type}`
        )
        .then((response) => {
          this.syncLoading = false;
          if (response.status === 200) {
            this.getAppUsageReport();
          }
        })
        .catch((error) => {
          this.syncLoading = false;
          console.log(error.response.data?.meassage);
          this.$partnerSwal.errorToast.fire({
            title: 'Failed to sync attendance data!',
            text: error.response.data?.message
          });
        });
    }
  }
};
</script>

<style scoped>
.page-heading h2 {
  font-size: 20px;
  font-weight: 500;
  line-height: 24px;
}
.action-bar {
  display: flex;
  justify-content: end;
  float: right;
}
.action-bar .dp__main,
.action-bar .btn,
.action-bar input,
.action-bar select {
  margin-left: 10px;
}
.action-bar .dp__main {
  width: 150px;
}
.usage-count {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  margin: 13px 0;
}
.usage-count .count {
  min-width: 180px;
  width: 20%;
  padding: 6px;
  cursor: pointer;
}
.usage-count .card {
  border-radius: 8px;
  text-align: center;
}
.usage-count .count:first-child {
  padding-left: 0;
}
.usage-count .count:last-child {
  padding-right: 0;
}
.partner-card .card-body {
  padding-bottom: 4px;
}
.table {
  font-size: 13px;
  font-weight: 400;
}

.table thead > tr > th {
  vertical-align: bottom;
  border-bottom: 2px solid #f8f9fa;
  background: #f3f3f3;
}

.table tbody > tr > td {
  vertical-align: middle;
}
.alert {
  margin-bottom: 0;
}
</style>
